// PAY_TAX_VIEW_v1 (Toby 20.10.2026: Steuerberater sieht „Kasten wie Michi“) -- der Kasten „Vergütung laut Vertrag aus
// der Kasse“ (VERGUETUNG_KASSE_v1, 14.10.) als EINE Komponente: Inhaber (Pro -> Mitarbeiter -> Provisionen) und
// Steuerberatung (Steuerberater-Bereich -> Provisionen) sehen dieselben Zahlen aus GET /api/employees/pay.
// audience "owner": Knopf zur Kasse + Hinweis „nur du als Inhaber“; audience "tax": nur lesen, Grundlage für den Lohn.
// Ohne Hooks -> auf Modul-Ebene, Aufruf als <PayContractCard … /> (Lehre EMP_MODAL_STABLE_v1).
import { COLORS } from "../core/config.jsx";
import { Btn, Card, Wm } from "../core/ui.jsx";

const _eur = v => (Number(v) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const _cEur = c => _eur((Number(c) || 0) / 100);
const _d = iso => String(iso || "").split("-").reverse().join(".");

// Zeilen mit Vertrags-Vergütung oder Kassen-Umsatz (gleiche Regel wie bisher in employees.jsx)
export const payContractRows = (pay) => ((pay && pay.employees) || []).filter(e =>
  (e.active !== false && (e.target_cents != null || Number(e.bonus_pct) > 0))
  || Number(e.actual_cents) || Number(e.product_cents) || Number(e.product_commission_cents));

export const PayContractCard = ({ pay, monthLabel, employees, audience = "owner", onOpenKasse }) => {
  if (!pay || pay.loading || !pay.available) return null;
  const tax = audience === "tax";
  const rows = payContractRows(pay);
  // Steuerberatung: Salon mit Kasse, aber ohne eingetragene Vergütung -> kein leerer Kasten
  if (tax && pay.ok && !rows.some(r => r.target_cents != null || Number(r.bonus_pct) > 0 || Number(r.product_commission_pct) > 0)) return null;
  const istLabel = pay.scope === "all" ? "Umsatz" : "Dienstleistung";
  const th = { padding: "8px 10px", color: COLORS.textLight, fontWeight: 600, whiteSpace: "nowrap" };
  const td = { padding: "10px 10px", textAlign: "right", whiteSpace: "nowrap" };
  const sm = { fontSize: 10, color: COLORS.textLight, fontWeight: 500 };
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Vergütung laut Vertrag{tax ? <> (<Wm /> Kasse)</> : ""} — {monthLabel}</h3>
        {!tax && <Btn size="sm" variant="secondary" onClick={onOpenKasse}>Gehalt, Soll und Bonus in der Kasse ändern</Btn>}
      </div>
      {!pay.ok ? (
        <div style={{ padding: 16, fontSize: 13, color: COLORS.textLight }}>Die Kasse ist gerade nicht erreichbar. Bitte später noch einmal öffnen.</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 16, fontSize: 13, color: COLORS.textLight }}>Noch keine Vergütung eingetragen. Gehalt, Faktor, Bonus und Warenprovision trägst du in der Kasse ein (Knopf oben).</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                {["Mitarbeiter", "Soll", istLabel, "bis zum Soll", "Bonus", "Ware", "Warenprovision"].map((h, i) => (
                  <th key={h} style={{ ...th, textAlign: i === 0 ? "left" : "right" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const emp = (employees || []).find(e => Number(e.id) === Number(r.pro_employee_id));
                const hasT = r.target_cents != null;
                const rest = Number(r.remaining_to_target_cents) || 0;
                return (
                  <tr key={r.pro_employee_id || r.name} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: (emp && emp.color) || COLORS.textLight, flexShrink: 0 }} />
                        <strong>{(emp && emp.name) || r.name}</strong>
                      </div>
                    </td>
                    <td style={td}>{hasT ? _cEur(r.target_cents) : "–"}{hasT && r.factor ? <div style={sm}>Faktor {String(r.factor).replace(".", ",")}</div> : null}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{_cEur(r.actual_cents != null ? r.actual_cents : r.service_cents)}</td>
                    <td style={td}>{!hasT ? "–" : rest > 0 ? _cEur(rest) : <span style={{ color: COLORS.success, fontWeight: 700 }}>erreicht ✓</span>}</td>
                    <td style={{ ...td, fontWeight: 700, color: Number(r.bonus_cents) > 0 ? COLORS.success : COLORS.text }}>{Number(r.bonus_pct) > 0 ? _cEur(r.bonus_cents) : "–"}{Number(r.bonus_pct) > 0 ? <div style={sm}>{Number(r.bonus_pct)} % über Soll</div> : null}</td>
                    <td style={td}>{_cEur(r.product_cents)}</td>
                    <td style={{ ...td, fontWeight: 700, color: COLORS.primary }}>{Number(r.product_commission_pct) > 0 ? _cEur(r.product_commission_cents) : "–"}{Number(r.product_commission_pct) > 0 ? <div style={sm}>{Number(r.product_commission_pct)} %</div> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize: 11, color: COLORS.textLight, lineHeight: 1.5, marginTop: 10 }}>
        Kassierter Umsatz {pay.basis === "brutto" ? "brutto" : "netto"} laut <Wm /> Kasse{pay.counted_until ? `, gezählt bis ${_d(pay.counted_until)}` : ""}. Soll = Bruttolohn × Faktor. Der Bonus gilt für den Teil über dem Soll des ganzen Monats. Trinkgeld zählt nicht.
        {tax
          ? <> <b>Grundlage für die Lohnabrechnung</b> (Vergütung laut Vertrag). Die Termin-Tabelle darunter dient nur zum Abgleich. {pay.is_current ? "Der laufende Monat ändert sich noch, endgültig ist der ganze Kalendermonat." : ""}</>
          : " Diese Zahlen siehst du als Inhaber und deine Steuerberatung (für die Lohnabrechnung)."}
      </div>
    </Card>
  );
};
